import { get, filter, find, findLast, findIndex, pick, last } from 'lodash';
import { createSelector } from 'reselect';

const PROGRAM_NAV_FIELDS = ['startTime', 'title', 'titles', 'channelId'];

const getChannelsSelector = (state) => state.channels;

export const getLastFocusSelector = createSelector(getChannelsSelector, (channels) => channels.lastFocus);

export const getIsLoadingSelector = createSelector(getChannelsSelector, (channels) => channels.isLoading);

export const getChannelsDataSelector = createSelector(getChannelsSelector, (channels) => channels.data);

export const getLiveEPGErrorTimestamp = createSelector(
  getChannelsSelector,
  ({ lastEPGUpdateErrorTimestamp }) => lastEPGUpdateErrorTimestamp,
);

export const getAvailableChannelsDataSelector = createSelector(getChannelsDataSelector, (channels) =>
  filter(channels, { playStatus: 'OK' }),
);

export const getFavoriteChannelsDataSelector = createSelector(getAvailableChannelsDataSelector, (channels) =>
  channels.filter((channel) => channel.favorite),
);

export const getCurrentChannelByPropsSelector = createSelector(
  getAvailableChannelsDataSelector,
  (_state, props) => parseInt(props.match.params.channelId, 10) || null,
  (availableChannels, id) => (id ? find(availableChannels, { id }) : null),
);

export const getCurrentProgramByPropsSelector = createSelector(
  getCurrentChannelByPropsSelector,
  (_state, props) => props.match.params.startTime || null,
  (channel, startTime) => {
    if (!channel) return null;

    const channelMetadata = pick(channel, ['title', 'titles', 'logoUrl', 'hideLogo', 'favorite']);
    const currProgram = find(channel.epg, { startTime });

    return currProgram ? { ...currProgram, channelMetadata } : null;
  },
);

export const getChannelNavigationSelector = createSelector(
  getAvailableChannelsDataSelector,
  getCurrentChannelByPropsSelector,
  getCurrentProgramByPropsSelector,
  (channels, currChannel, nowPlayingProgram) => {
    if (!currChannel) return {};
    // This is EPG store design mistake. EPG shouldn't be stored with days title.
    const epg = filter(currChannel.epg, { isHeader: false });

    const navigation = {};

    // in-player channel navigation aren't availbale for adult channels
    if (!currChannel.isAdult) {
      const currChannelIndex = findIndex(channels, { id: currChannel.id });
      // It trying to find closest non-adult channel from both sides
      const currentChannel = find(channels, { id: currChannel.id });
      const prevChannel = findLast(channels, { isAdult: false }, currChannelIndex - 1);

      const nextChannel = find(
        channels,
        { isAdult: false },
        //Fix infinite zapping on DOWN, CH_DOWN
        currChannelIndex + 1 >= channels.length ? 0 : currChannelIndex + 1,
      );

      navigation.prevChannel = prevChannel ? prevChannel : null;
      navigation.nextChannel = nextChannel ? nextChannel : null;
      navigation.currentChannel = currentChannel ? currentChannel : null;
    }

    if (nowPlayingProgram) {
      const currentChannel = find(channels, { id: currChannel.id });
      const currProgramIndex = findIndex(epg, { id: nowPlayingProgram.id });
      let prevProgram = epg[currProgramIndex - 1] || {};
      let nextProgram = epg[currProgramIndex + 1] || {};

      if (currChannel.isVod && currProgramIndex === 0) {
        prevProgram = last(epg);
      }
      if (currChannel.isVod && !epg[currProgramIndex + 1]) {
        nextProgram = epg[0] || {};
      }

      navigation.currentChannel = currentChannel || null;

      if (['CATCHUP', 'LIVE_CATCHUP'].includes(prevProgram.state)) {
        navigation.prevProgram = pick(prevProgram, PROGRAM_NAV_FIELDS);
      }
      if (['CATCHUP', 'LIVE_CATCHUP'].includes(nextProgram.state)) {
        navigation.nextProgram = pick(nextProgram, PROGRAM_NAV_FIELDS);
        navigation.nextProgram.isLive = nextProgram.id === currChannel.currentProgram?.id;
        navigation.nextProgram.isDVR = !!currChannel?.tvServices?.catchupTv;
      }
    } else if (!nowPlayingProgram && currChannel.tvServices.catchupTv) {
      const currentProgramId = (currChannel.currentProgram || {}).id;
      const prevProgram = get(epg, [findIndex(epg, { id: currentProgramId }) - 1], {});

      if (prevProgram.state === 'CATCHUP') {
        navigation.prevProgram = pick(prevProgram, PROGRAM_NAV_FIELDS);
      }
    }

    return navigation;
  },
);
